import { useEffect, useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { ArrowLeft, Crown, DollarSign, Save } from 'lucide-react'
import { toast } from 'sonner'
import MainLayout from '@/components/Layout/MainLayout'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Label } from '@/components/ui/Label'
import { Input } from '@/components/ui/Input'
import { Switch } from '@/components/ui/Switch'
import { godModePlans } from '@/routeHelpers'
import type { CurrentUser } from '@/types/shared'

interface PlanEntitlement {
  id: string
  entitlement_id: string
  key: string
  name: string
  value: number | boolean
  value_type: string
}

interface PlanPrice {
  id: string
  interval: string
  amount_cents: number
  currency: string
  stripe_price_id: string | null
  active: boolean
}

interface Plan {
  id: string
  name: string
  slug: string
  description: string
  internal: boolean
  active: boolean
  position: number
  entitlements: PlanEntitlement[]
  prices: PlanPrice[]
}

interface Entitlement {
  id: string
  key: string
  name: string
  value_type: string
  description: string
}

interface PlanShowProps {
  current_user: CurrentUser
  god_mode: boolean
  plan: Plan
  entitlements: Entitlement[]
}

export default function PlanShow({ current_user, god_mode, plan, entitlements }: PlanShowProps) {
  const { flash } = usePage<{ flash: { notice?: string; alert?: string } }>().props
  const [editingPrice, setEditingPrice] = useState<{ interval: string; amount: string } | null>(null)

  useEffect(() => {
    if (flash?.notice) {
      toast.success(flash.notice)
    }
  }, [flash?.notice])

  const handleEntitlementUpdate = (key: string, value: string) => {
    const parsedValue = value === 'true' ? true : value === 'false' ? false : parseInt(value, 10)
    router.patch(godModePlans.updateEntitlement.path({ id: plan.id }), {
      entitlement_key: key,
      value: parsedValue,
    })
  }

  const handlePriceUpdate = (interval: string, amountCents: number) => {
    router.patch(godModePlans.updatePrice.path({ id: plan.id }), {
      interval,
      amount_cents: amountCents,
    })
    setEditingPrice(null)
  }

  const getEntitlementValue = (key: string): number | boolean | undefined => {
    const ent = plan.entitlements.find(e => e.key === key)
    return ent?.value
  }

  const getPrice = (interval: string): PlanPrice | undefined => {
    return plan.prices.find(p => p.interval === interval)
  }

  return (
    <>
      <Head title={`${plan.name} - God Mode`} />
      <MainLayout
        user={current_user}
        breadcrumbs={[
          { label: 'God Mode' },
          { label: 'Plans', href: godModePlans.index.path() },
          { label: plan.name },
        ]}
        godMode={god_mode}
      >
        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-3xl mx-auto space-y-6">
            <Link
              href={godModePlans.index.path()}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <ArrowLeft className="size-4" />
              Back to plans
            </Link>

            <div className="flex items-center gap-4">
              <div className="size-16 rounded-xl flex items-center justify-center bg-amber-500/20">
                <Crown className="size-8 text-amber-500" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold">{plan.name}</h1>
                <p className="text-muted-foreground">{plan.slug}</p>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Plan Details</CardTitle>
                <CardDescription>Basic plan information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Status</Label>
                    <p className="font-medium">{plan.active ? 'Active' : 'Inactive'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Type</Label>
                    <p className="font-medium">{plan.internal ? 'Internal' : 'Public'}</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-muted-foreground">Description</Label>
                    <p className="font-medium">{plan.description || 'No description'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="size-5 text-green-500" />
                  Pricing
                </CardTitle>
                <CardDescription>Monthly and yearly prices</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {['monthly', 'yearly'].map((interval) => {
                  const price = getPrice(interval)
                  const isEditing = editingPrice?.interval === interval

                  return (
                    <div key={interval} className="flex items-center gap-4">
                      <div className="w-24">
                        <Label>{interval === 'monthly' ? 'Monthly' : 'Yearly'}</Label>
                      </div>
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={editingPrice.amount}
                            onChange={(e) => setEditingPrice({ interval, amount: e.target.value })}
                            className="w-32"
                            placeholder="Amount in cents"
                          />
                          <Button
                            size="sm"
                            onClick={() => handlePriceUpdate(interval, parseInt(editingPrice.amount, 10))}
                          >
                            <Save className="size-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingPrice(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {price ? `$${(price.amount_cents / 100).toFixed(2)}` : 'Not set'}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingPrice({
                              interval,
                              amount: price?.amount_cents?.toString() || ''
                            })}
                          >
                            Edit
                          </Button>
                          {price?.stripe_price_id && (
                            <span className="text-xs text-muted-foreground">
                              Stripe: {price.stripe_price_id.slice(0, 20)}...
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Entitlements</CardTitle>
                <CardDescription>Features and limits for this plan</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {entitlements.map((ent) => {
                  const currentValue = getEntitlementValue(ent.key)
                  const hasValue = currentValue !== undefined

                  return (
                    <div key={ent.id} className="flex items-center gap-4">
                      <div className="flex-1">
                        <Label>{ent.name}</Label>
                        <p className="text-xs text-muted-foreground">{ent.key}</p>
                      </div>
                      <div className="w-40">
                        {ent.value_type === 'boolean' ? (
                          <Switch
                            checked={currentValue as boolean || false}
                            onCheckedChange={(checked: boolean) =>
                              handleEntitlementUpdate(ent.key, String(checked))
                            }
                          />
                        ) : (
                          <Input
                            type="number"
                            value={hasValue ? (currentValue as number) : ''}
                            onChange={(e) => handleEntitlementUpdate(ent.key, e.target.value)}
                            placeholder="-1 for unlimited"
                            className="w-full"
                          />
                        )}
                      </div>
                    </div>
                  )
                })}
              </CardContent>
            </Card>
          </div>
        </div>
      </MainLayout>
    </>
  )
}
